import React, { useEffect, useState, useRef } from 'react';
import { updateProfile, changePassword, uploadProfileImage, deleteProfileImage, authHeaders } from '../utils/authService';
import { getAvailability, setAvailability, getConnections } from '../utils/sharingService';
import Header from '../components/Header';
import ProfileImage from '../components/ProfileImage';
import LoadingProgress from '../components/LoadingProgress';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  content: {
    maxWidth: 980,
    margin: '0 auto',
    padding: '32px 24px 48px',
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    marginBottom: 24,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 20,
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 12,
    padding: 24,
  },
  sectionTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 18,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 13px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 110,
    resize: 'vertical',
    padding: '11px 13px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  readOnly: {
    opacity: 0.75,
    cursor: 'not-allowed',
  },
  actions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  button: {
    padding: '10px 18px',
    background: 'var(--color-primary)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '10px 18px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  helper: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text-dim)',
    lineHeight: 1.5,
    marginTop: -6,
    marginBottom: 14,
  },
  success: {
    marginTop: 12,
    padding: '10px 12px',
    background: 'rgba(39, 174, 96, 0.12)',
    border: '1px solid rgba(39, 174, 96, 0.24)',
    borderRadius: 8,
    color: '#27ae60',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
  },
  error: {
    marginTop: 12,
    padding: '10px 12px',
    background: 'rgba(231, 76, 60, 0.12)',
    border: '1px solid rgba(231, 76, 60, 0.24)',
    borderRadius: 8,
    color: '#e74c3c',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
  },
  loading: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    textAlign: 'center',
    padding: 60,
  },
};

const INITIAL_PROFILE = {
  displayName: '',
  teamName: '',
  phone: '',
  location: '',
  primaryCar: '',
  racingClass: '',
  homeTrack: '',
  bio: '',
  units: 'imperial',
};

export default function ProfilePage({ user, onLogout, onAdmin, onDashboard, onProfile, onBack, targetUser, onUserUpdate }) {
  const isAdminEdit = !!targetUser;
  const editUid = targetUser?.uid || user.uid;
  const editEmail = targetUser?.email || user.email;
  const [profile, setProfile] = useState(INITIAL_PROFILE);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [sharingAvailable, setSharingAvailable] = useState(true);
  const [connectionCount, setConnectionCount] = useState(0);
  const [profileImageName, setProfileImageName] = useState('');
  const [imageKey, setImageKey] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [imageSuccess, setImageSuccess] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    async function loadSharing() {
      try {
        const [avail, conns] = await Promise.all([getAvailability(), getConnections()]);
        setSharingAvailable(avail);
        setConnectionCount(conns.length);
      } catch (err) {
        console.error('Failed to load sharing settings:', err);
      }
    }
    loadSharing();
  }, []);

  const handleToggleSharing = async () => {
    try {
      const newVal = !sharingAvailable;
      await setAvailability(newVal);
      setSharingAvailable(newVal);
    } catch (err) {
      console.error('Failed to toggle sharing:', err);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    setImageError('');
    setImageSuccess('');
    try {
      let result;
      if (isAdminEdit) {
        // Use admin endpoint for another user's image
        const reader = new FileReader();
        result = await new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              const res = await fetch(`/api/admin/users/${editUid}/profile-image`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ image: reader.result }),
              });
              const data = await res.json();
              if (!res.ok) reject(new Error(data.error || 'Upload failed'));
              else resolve(data);
            } catch (err) { reject(err); }
          };
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });
      } else {
        result = await uploadProfileImage(file);
      }
      setProfileImageName(result.profileImage);
      setImageKey((k) => k + 1);
      setImageSuccess('Photo updated.');
      if (!isAdminEdit && onUserUpdate) onUserUpdate({ profileImage: result.profileImage });
    } catch (err) {
      setImageError(err.message || 'Failed to upload image.');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageDelete = async () => {
    setImageUploading(true);
    setImageError('');
    setImageSuccess('');
    try {
      if (isAdminEdit) {
        const res = await fetch(`/api/admin/users/${editUid}/profile-image`, {
          method: 'DELETE',
          headers: authHeaders(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to delete image');
      } else {
        await deleteProfileImage();
      }
      setProfileImageName('');
      setImageKey((k) => k + 1);
      setImageSuccess('Photo removed.');
      if (!isAdminEdit && onUserUpdate) onUserUpdate({ profileImage: '' });
    } catch (err) {
      setImageError(err.message || 'Failed to remove image.');
    } finally {
      setImageUploading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      setProfileLoading(true);
      setProfileError('');

      try {
        const url = isAdminEdit
          ? `/api/admin/users/${editUid}/profile`
          : '/api/auth/me';
        const token = localStorage.getItem('rv_token');
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (res.ok) {
          const data = await res.json();
          setProfile({
            displayName: data.displayName || (isAdminEdit ? '' : user.displayName) || '',
            teamName: data.teamName || '',
            phone: data.phone || '',
            location: data.location || '',
            primaryCar: data.primaryCar || '',
            racingClass: data.racingClass || '',
            homeTrack: data.homeTrack || '',
            bio: data.bio || '',
            units: data.units || 'imperial',
          });
          setProfileImageName(data.profileImage || '');
        } else {
          setProfile({
            ...INITIAL_PROFILE,
            displayName: isAdminEdit ? '' : (user.displayName || ''),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setProfileError('Failed to load profile. Please refresh and try again.');
        }
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
        }
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user, isAdminEdit, editUid]);

  const handleProfileChange = (field) => (e) => {
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const profileData = {
        displayName: profile.displayName.trim(),
        teamName: profile.teamName.trim(),
        phone: profile.phone.trim(),
        location: profile.location.trim(),
        primaryCar: profile.primaryCar.trim(),
        racingClass: profile.racingClass.trim(),
        homeTrack: profile.homeTrack.trim(),
        bio: profile.bio.trim(),
        units: profile.units || 'imperial',
      };

      if (isAdminEdit) {
        const res = await fetch(`/api/admin/users/${editUid}/profile`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(profileData),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to update profile');
      } else {
        await updateProfile(profileData);
      }

      setProfileSuccess('Profile updated.');
      if (!isAdminEdit && onUserUpdate) onUserUpdate({ units: profileData.units });
    } catch (err) {
      setProfileError(err.message || 'Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      if (!isAdminEdit && !passwordForm.currentPassword) {
        throw new Error('Please enter your current password.');
      }
      if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
        throw new Error('Please complete all password fields.');
      }
      if (passwordForm.newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters.');
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error('New passwords do not match.');
      }

      if (isAdminEdit) {
        const res = await fetch(`/api/admin/users/${editUid}/password`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ password: passwordForm.newPassword }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to update password');
      } else {
        await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      }

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordSuccess('Password updated.');
      if (!isAdminEdit && onUserUpdate) {
        onUserUpdate({ mustChangePassword: false });
      }
    } catch (err) {
      setPasswordError(err.message || 'Failed to update password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (profileLoading) {
    return (
      <div style={styles.page}>
        <Header
          user={user}
          onLogout={onLogout}
          onAdmin={onAdmin}
          onDashboard={onDashboard}
          onProfile={onProfile}
          onBack={onBack}
          showBack={!!onBack}
        />
        <div style={styles.content}>
          <LoadingProgress label="Loading profile" />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page} data-doc-id="profile-page">
      <Header
        user={user}
        onLogout={onLogout}
        onAdmin={onAdmin}
        onDashboard={onDashboard}
        onProfile={onProfile}
        onBack={onBack}
        showBack={!!onBack}
      />
      <div style={styles.content}>
        <div style={styles.title}>
          {isAdminEdit ? `EDIT PROFILE: ${editEmail}` : 'PROFILE SETTINGS'}
        </div>
        <div style={styles.grid}>
          <form style={styles.card} onSubmit={handleSaveProfile} data-doc-id="profile-form">
            <div style={styles.sectionTitle}>Profile</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <ProfileImage key={imageKey} uid={editUid} profileImage={profileImageName} size={80} />
              <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <button
                    type="button"
                    style={styles.button}
                    disabled={imageUploading}
                    onClick={() => fileInputRef.current?.click()}
                    data-doc-id="profile-upload-photo"
                  >
                    {imageUploading ? 'Uploading...' : 'Upload Photo'}
                  </button>
                  {profileImageName && (
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      disabled={imageUploading}
                      onClick={handleImageDelete}
                      data-doc-id="profile-remove-photo"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-text-dim)' }}>
                  JPEG, PNG, or WebP. Max 2MB.
                </div>
                {imageSuccess && <div style={{ ...styles.success, marginTop: 6, padding: '6px 10px' }}>{imageSuccess}</div>}
                {imageError && <div style={{ ...styles.error, marginTop: 6, padding: '6px 10px' }}>{imageError}</div>}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                style={{ ...styles.input, ...styles.readOnly }}
                type="email"
                value={editEmail || ''}
                disabled
              />
            </div>

            {user.role === 'admin' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>User ID</label>
                <input
                  style={{ ...styles.input, ...styles.readOnly }}
                  type="text"
                  value={editUid || ''}
                  disabled
                />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Display Name</label>
              <input
                data-doc-id="profile-display-name"
                style={styles.input}
                type="text"
                value={profile.displayName}
                onChange={handleProfileChange('displayName')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Team Name</label>
              <input
                style={styles.input}
                type="text"
                value={profile.teamName}
                onChange={handleProfileChange('teamName')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input
                style={styles.input}
                type="text"
                value={profile.phone}
                onChange={handleProfileChange('phone')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Location</label>
              <input
                style={styles.input}
                type="text"
                value={profile.location}
                onChange={handleProfileChange('location')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Primary Car</label>
              <input
                style={styles.input}
                type="text"
                value={profile.primaryCar}
                onChange={handleProfileChange('primaryCar')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Racing Class</label>
              <input
                style={styles.input}
                type="text"
                value={profile.racingClass}
                onChange={handleProfileChange('racingClass')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Home Track</label>
              <input
                style={styles.input}
                type="text"
                value={profile.homeTrack}
                onChange={handleProfileChange('homeTrack')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Bio</label>
              <textarea
                style={styles.textarea}
                value={profile.bio}
                onChange={handleProfileChange('bio')}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Units</label>
              <div style={{ display: 'flex', gap: 0 }}>
                {[
                  { value: 'imperial', label: 'Imperial (mph, mi)' },
                  { value: 'metric', label: 'Metric (km/h, km)' },
                ].map((opt, i) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setProfile((prev) => ({ ...prev, units: opt.value }))}
                    style={{
                      padding: '9px 16px',
                      background: profile.units === opt.value ? 'var(--color-primary)' : 'var(--color-bg)',
                      border: '1px solid ' + (profile.units === opt.value ? 'var(--color-primary)' : 'var(--color-border)'),
                      borderRadius: i === 0 ? '8px 0 0 8px' : '0 8px 8px 0',
                      color: profile.units === opt.value ? '#fff' : 'var(--color-text)',
                      fontFamily: 'var(--font-heading)',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.actions}>
              <button type="submit" style={styles.button} disabled={profileSaving} data-doc-id="profile-save">
                {profileSaving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>

            {profileSuccess && <div style={styles.success}>{profileSuccess}</div>}
            {profileError && <div style={styles.error}>{profileError}</div>}
          </form>

          <form style={styles.card} onSubmit={handlePasswordSubmit} data-doc-id="profile-security-form">
            <div style={styles.sectionTitle}>Security</div>
            <div style={styles.helper}>
              {isAdminEdit
                ? `Set a new password for ${editEmail}.`
                : 'Update your password here. Your email address is fixed and cannot be changed from this screen.'}
            </div>

            {!isAdminEdit && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Current Password</label>
              <input
                data-doc-id="profile-current-password"
                style={styles.input}
                type="password"
                value={passwordForm.currentPassword}
                onChange={handlePasswordChange('currentPassword')}
                autoComplete="current-password"
              />
            </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <input
                data-doc-id="profile-new-password"
                style={styles.input}
                type="password"
                value={passwordForm.newPassword}
                onChange={handlePasswordChange('newPassword')}
                autoComplete="new-password"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm New Password</label>
              <input
                data-doc-id="profile-confirm-password"
                style={styles.input}
                type="password"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordChange('confirmPassword')}
                autoComplete="new-password"
              />
            </div>

            <div style={styles.actions}>
              <button type="submit" style={styles.button} disabled={passwordSaving} data-doc-id="profile-change-password">
                {passwordSaving ? 'Updating...' : 'Change Password'}
              </button>
              {onBack && (
                <button type="button" style={styles.secondaryButton} onClick={onBack} data-doc-id="profile-back">
                  Back
                </button>
              )}
            </div>

            {passwordSuccess && <div style={styles.success}>{passwordSuccess}</div>}
            {passwordError && <div style={styles.error}>{passwordError}</div>}
          </form>

          <div style={styles.card} data-doc-id="profile-sharing-card">
            <div style={styles.sectionTitle}>Data Sharing</div>
            <div style={styles.helper}>
              When enabled, other RaceVoice drivers can find you and send connection requests.
              Your session data is only shared after you accept a request.
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
              <button
                type="button"
                onClick={handleToggleSharing}
                data-doc-id="profile-sharing-toggle"
                style={{
                  width: 48,
                  height: 26,
                  borderRadius: 13,
                  border: 'none',
                  background: sharingAvailable ? '#2ecc71' : 'var(--color-border)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: 3,
                  left: sharingAvailable ? 25 : 3,
                  transition: 'left 0.2s',
                }} />
              </button>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-text)' }}>
                {sharingAvailable ? 'Available for sharing' : 'Not visible to other drivers'}
              </span>
            </div>

            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-text-dim)' }}>
              Active connections: <strong style={{ color: 'var(--color-text)' }}>{connectionCount}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
